import { createContext } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

export const CatalogContext = createContext();

export function CatalogProvider({
  children,
  title,
  langSet = ['ko', 'en'],
  pageMap,
  defaultLang = langSet[0],
  width = 1920,
  height = 930,
  pagingSpeed = 500,
  autoplay = {
    delay: 7000,
    loop: true,
  },
  responsiveMode = true,
  breakpoint = 768,
}) {
  const navigate = useNavigate();
  const params = useParams();

  const lang = params.lang;
  const pageTotal = pageMap.length;
  const pageNum = parseInt(params.pageNum, 10);
  const pagingPath = { prev: null, next: null };
  const [activePageInfo, setActivePageInfo] = useState(null);
  const [pagingDirection, setPagingDirection] = useState(null);

  // URL 파라미터 유효성 검증
  useEffect(() => {
    if (!lang || lang === 'undefined' || !langSet.includes(lang)) {
      navigate(`/${defaultLang}/1`);
    } else if (!pageNum || pageNum === NaN || pageNum <= 0) {
      navigate(`/${lang}/1`);
    } else if (pageNum > pageTotal) {
      navigate(`/${lang}/${pageTotal}`);
    }
  }, [defaultLang]);

  // 이전, 다음 페이지 경로 저장
  if (pageNum > 1) {
    pagingPath.prev = `/${lang}/${pageNum - 1}`;
  } else {
    pagingPath.prev = null;
  }
  if (pageNum < pageTotal) {
    pagingPath.next = `/${lang}/${pageNum + 1}`;
  } else {
    pagingPath.next = null;
  }

  // 애니메이션 트리거
  const [animeReady, setAnimeReady] = useState(false);

  useEffect(() => setAnimeReady(false), [pageNum]);

  /**
   * 카탈로그 페이징 함수
   * dir 인자에 따라 이전, 다음페이지 또는 특정 번호의 페이지로 이동
   * @param {string, number} dir
   */
  const paging = dir => {
    switch (dir) {
      case 'prev':
        if (!pagingPath.prev) {
          console.log('no prev page');
          return;
        }
        navigate(pagingPath.prev);
        setPagingDirection('prev');
        break;
      case 'next':
        if (!pagingPath.next) {
          console.log('no next page');
          return;
        }
        navigate(pagingPath.next);
        setPagingDirection('next');
        break;
      default:
        if (typeof dir !== 'number') {
          throw new Error('dir is NaN');
        }
        setAnimeReady(false);
        navigate(`/${lang}/${dir}`);
        setPagingDirection('jump');
        break;
    }
  };

  // 키보드 페이징 컨트롤
  useEffect(() => {
    const keydownHandler = e => {
      const key = e.key;
      const directionMap = {
        ArrowLeft: 'prev',
        ArrowUp: 'prev',
        ArrowRight: 'next',
        ArrowDown: 'next',
      };

      const direction = directionMap[key];
      if (direction) {
        paging(direction);
      }
    };
    window.addEventListener('keydown', keydownHandler);

    return () => {
      window.removeEventListener('keydown', keydownHandler);
    };
  }, [pagingPath]);

  // 오토플레이
  const [autoplayRunning, setAutoplayRunning] = useState(false);
  const toggleAutoplay = () => {
    if (!autoplayRunning && !pagingPath.next) {
      paging(1);
    }
    setAutoplayRunning(prev => !prev);
  };
  const startAutoplay = () => {
    if (!autoplayRunning && !pagingPath.next) {
      paging(1);
    }
    setAutoplayRunning(true);
  };
  const stopAutoplay = () => {
    setAutoplayRunning(false);
  };

  useEffect(() => {
    // console.log(autoplayRunning);
  }, [autoplayRunning]);

  useEffect(() => {
    let autoplayTimer;

    const handleAutoplayTimer = () => {
      autoplayTimer = setTimeout(() => {
        if (autoplay.loop && !pagingPath.next) {
          paging(1);
        } else if (!autoplay.loop && !pagingPath.next) {
          clearTimeout(autoplayTimer);
          stopAutoplay();
          return;
        } else {
          paging('next');
        }
        handleAutoplayTimer();
      }, autoplay.delay);
    };

    if (autoplayRunning) {
      handleAutoplayTimer();
    } else {
      clearTimeout(autoplayTimer);
    }

    return () => {
      clearTimeout(autoplayTimer);
    };
  }, [autoplayRunning, pagingPath]);

  /**
   * 반응형 모드 설정
   * catalogOptions.responsiveMode가 true이면 동작함.
   * catalogOptions.breakpoint 보다 window.width가 작으면 responsiveMode 상태로 변환
   * 변환된 상태는 반응형 모드에서 별도 처리되어야 할 부분에서 사용 ex) wheel control, scaler 등
   */
  const [isResponsiveMode, setIsResponsiveMode] = useState(null);

  const getResponsiveMode = () => {
    const viewportWidth = window.innerWidth;
    if (responsiveMode && viewportWidth <= breakpoint) {
      setIsResponsiveMode(true);
    } else {
      setIsResponsiveMode(false);
    }
  };

  useEffect(() => {
    getResponsiveMode();
    window.addEventListener('resize', getResponsiveMode);
    return () => {
      window.removeEventListener('resize', getResponsiveMode);
    };
  }, []);

  return (
    <CatalogContext.Provider
      value={{
        title,
        lang,
        width,
        height,
        breakpoint,
        isResponsiveMode,
        pagingSpeed,
        pageMap,
        pageTotal,
        pageNum,
        pagingPath,
        pagingDirection,
        setPagingDirection,
        activePageInfo,
        setActivePageInfo,
        autoplay,
        autoplayRunning,
        toggleAutoplay,
        startAutoplay,
        stopAutoplay,
        paging,
        animeReady,
        setAnimeReady,
      }}
    >
      {children}
    </CatalogContext.Provider>
  );
}
